import type { CSSProperties } from 'react';
import { IconButton } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { PauseIcon } from 'src/svgs/PauseIcon';
import { DeleteIcon, ProcessingIcon, ReloadIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdCampaignState,
  CampaignManagerClient,
  SponsoredListingsCampaign,
} from 'src/WebApiController';

export type UpdateCampaignStateButtonProps = {
  campaign: SponsoredListingsCampaign;
  state: AdCampaignState;
  onBeforePause?: () => void;
  onAfterPause?: (success: boolean) => void;
  disabled?: boolean;
  className?: string;
  buttonStyle?: CSSProperties;
  iconOnly?: boolean;
};

export const UpdateCampaignStateButton = ({
  campaign,
  state,
  onBeforePause,
  onAfterPause,
  disabled,
  className,
  buttonStyle,
  iconOnly,
}: UpdateCampaignStateButtonProps) => {
  const { showErrorDialog, genericError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const onClickHandler = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    onBeforePause?.();
    await tryInvokeApi(
      async () => {
        if (campaign?.campaignId) {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const response = await client.updateCampaignState(
            campaign.campaignId,
            state
          );

          if (response.success) {
            onAfterPause?.(true);
          } else {
            showErrorDialog('updateCampaignState', {
              message: response.errorMessage || genericError,
            });
            onAfterPause?.(false);
          }
        }
      },
      (error) => {
        showErrorDialog('updateCampaignState', error);
        onAfterPause?.(false);
      }
    );
  };

  const Icon =
    state === AdCampaignState.Paused
      ? PauseIcon
      : state === AdCampaignState.Active
        ? ProcessingIcon
        : state === AdCampaignState.Deleted
          ? DeleteIcon
          : undefined;

  return iconOnly && Icon ? (
    <IconButton
      icon={
        <Icon withHoverEffect fill={vars.color.textBrandV2} align="middle" />
      }
      onClick={onClickHandler}
      disabled={disabled}
    />
  ) : (
    <Button
      className={className}
      style={buttonStyle}
      variant="outlineGray"
      shape="rect"
      onClick={onClickHandler}
      disabled={disabled}
    >
      <Content
        id={
          state === AdCampaignState.Paused
            ? ContentId.Pause
            : state === AdCampaignState.Active
              ? ContentId.Restart
              : state === AdCampaignState.Deleted
                ? ContentId.Archive
                : undefined
        }
      />
    </Button>
  );
};
